import styled from 'styled-components';
import useReportDataContext from '../../hooks/useReportDataContext';
import type { SummaryItem } from '../../types/report';
import LoadingAnimation from '../../../../components/loadingAnimation';

const ReportSummarySection = () => {
  const { summaryData, isSummaryLoading } = useReportDataContext();

  const abbreviateNumber = (num: number) => {
    const absoluteValue = Math.abs(num);
    // Only abbreviate once we exceed 999M — anything smaller shows in full.
    if (absoluteValue < 1e9) {
      return num.toLocaleString();
    }
    // Compact notation only has suffixes up to "T" (1e12); beyond that it
    // falls back to printing full digits, so use scientific notation instead.
    if (absoluteValue < 1e15) {
      return num.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 2 });
    }
    return num.toExponential(2);
  };

  const formatSummaryNumber = (item: SummaryItem, abbreviate: boolean) => {
    const num = parseFloat(item.value);
    const formatted = abbreviate ? abbreviateNumber(num) : num.toLocaleString();
    if (item.percentage) {
      return `${formatted} %`;
    }
    if (item.currency) {
      return `${item.currency} ${formatted}`;
    }
    return formatted;
  };

  const formatValue = (item: SummaryItem) => {
    const { value, type } = item;
    if (type === 'number') {
      return formatSummaryNumber(item, true);
    }
    if (type === 'minutes') {
      const totalMinutes = parseInt(value, 10);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = Math.floor(totalMinutes % 60);
      const seconds = Math.floor((totalMinutes * 60) % 60);

      return hours > 0
        ? `${hours}h ${minutes}m ${seconds}s`
        : minutes > 0
          ? `${minutes}m ${seconds}s`
          : `${seconds}s`;
    }
    return value;
  };

  const formatFullValue = (item: SummaryItem) => {
    if (item.type === 'number') {
      return formatSummaryNumber(item, false);
    }
    return formatValue(item);
  };

  // Show loading skeleton when summary is loading
  if (isSummaryLoading) {
    return (
      <StyledSummarySection className="summary-section">
        <div className="loading-container">
          <LoadingAnimation />
        </div>
      </StyledSummarySection>
    );
  }

  if (!summaryData.length) return null;

  return (
    <StyledSummarySection className="summary-section">
      <div
        className="card-container"
        style={{ justifyContent: summaryData.length > 5 ? 'center' : 'space-around' }}
      >
        {summaryData.map((item, index) => (
          <div key={item.name} className="card" style={{ animationDelay: `${index * 0.1}s` }}>
            <div className="card-title">{item.name}</div>
            <div className="card-value-wrapper">
              <div className="card-value" title={formatFullValue(item)}>
                {formatValue(item)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </StyledSummarySection>
  );
};

export default ReportSummarySection;

const StyledSummarySection = styled.section`
  width: auto;
  padding: 24px;
  background: ${({ theme }) => theme.widget_group.background};
  border-radius: 12px;
  margin: 16px;
  margin-top: -8px;
  margin-bottom: 0;
  border: 1px solid ${({ theme }) => theme.border_color};

  @media (max-width: 768px) {
    padding: 16px;
  }

  .loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 150px;
  }

  .card-container {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;

    @media (min-width: 1280px) {
      & > * {
        flex: 0 1 calc(20% - 16px);
        min-width: 200px;
      }
    }

    @media (max-width: 1279px) {
      & > * {
        flex: 0 1 calc(33.333% - 14px);
        min-width: 200px;
      }
    }

    @media (max-width: 768px) {
      & > * {
        flex: 1 1 100%;
        max-width: 400px;
      }
    }
  }

  .card {
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.widget.background};
    border-radius: 12px;
    box-shadow:
      0 4px 6px rgba(0, 0, 0, 0.05),
      0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    animation: fadeIn 0.5s ease forwards;
    opacity: 0;
    transform: translateY(20px);
    border: 1px solid ${({ theme }) => theme.border_color};

    @keyframes fadeIn {
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow:
        0 6px 12px rgba(0, 0, 0, 0.15),
        0 2px 4px rgba(0, 0, 0, 0.1);
      border-color: ${({ theme }) => theme.tableBorder};
    }
  }

  .card-title {
    color: ${({ theme }) => theme.text};
    width: 100%;
    text-align: center;
    padding: 16px;
    background: ${({ theme }) => theme.widget.tableHeader};
    border-bottom: 1px solid ${({ theme }) => theme.border_color};
    border-radius: 12px 12px 0 0;
    font-weight: 600;
    font-size: 15px;
    line-height: 1.4;
    letter-spacing: 0.2px;
  }

  .card-value-wrapper {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    min-height: 100px;
    min-width: 0;
  }

  .card-value {
    font-weight: 700;
    font-size: 28px;
    line-height: 1.2;
    letter-spacing: -0.2px;
    text-align: center;
    color: ${({ theme }) => theme.primary_color};
    transition: color 0.2s ease;
    max-width: 100%;
    overflow-wrap: break-word;
    word-break: break-word;

    &:hover {
      color: ${({ theme }) => theme.primary_color};
      opacity: 0.8;
    }
  }
`;
